/**
 * ICE/transport fields for the voice_diag telemetry (voiceTelemetry.ts), parsed from a
 * PeerConnection getStats() report. Pure — no LiveKit / store imports — so it is unit-tested.
 *
 * Two prod facts (2026-10-15 TURN audit) shape this:
 *  1. Feed it the FULL `pc.getStats()` report, not a sender's `track.getRTCStatsReport()`.
 *     Chromium/WebKit run the stats-selection algorithm for a sender: only the SELECTED pair's
 *     local candidate survives, so per-type candidate counts were ≤ 1 (Firefox, which returns
 *     everything, was the only browser that ever showed cand_relay > 0), and a muted user with
 *     no published mic produced no ICE fields at all (94% of samples were blank).
 *  2. Our TURN relay reaches the SFU through a docker hairpin, so the SFU sees the relay as
 *     172.18.0.1:x and the browser learns a relay-based PEER-REFLEXIVE local candidate
 *     (candidateType 'prflx' + relayProtocol). Any local candidate carrying relayProtocol is
 *     a TURN path, whatever its candidateType says — it is reported as 'relay'.
 */

export interface IceFields {
  iceState: string
  selLocalType: string
  selRemoteType: string
  selProtocol: string
  selRelayProtocol: string
  availOutKbps: number
  candHost: number
  candSrflx: number
  candRelay: number
  candPrflx: number
}

interface StatsLike {
  forEach(cb: (s: any) => void): void
}

/** Candidate type with relay-derived peer-reflexive candidates folded into 'relay'. */
function candidateKind(c: any): string {
  if (c?.relayProtocol) return 'relay'
  return String(c?.candidateType || '')
}

export function parseIce(report: StatsLike): IceFields {
  const out: IceFields = {
    iceState: '',
    selLocalType: '',
    selRemoteType: '',
    selProtocol: '',
    selRelayProtocol: '',
    availOutKbps: 0,
    candHost: 0,
    candSrflx: 0,
    candRelay: 0,
    candPrflx: 0,
  }
  try {
    let selectedPairId = ''
    let transportState = ''
    const pairs: Record<string, any> = {}
    const locals: Record<string, any> = {}
    const remotes: Record<string, any> = {}
    report.forEach((s: any) => {
      if (s.type === 'transport') {
        if (s.selectedCandidatePairId) selectedPairId = s.selectedCandidatePairId
        if (s.iceState) transportState = s.iceState
      } else if (s.type === 'candidate-pair') {
        pairs[s.id] = s
      } else if (s.type === 'local-candidate') {
        locals[s.id] = s
        const t = candidateKind(s)
        if (t === 'host') out.candHost++
        else if (t === 'srflx') out.candSrflx++
        else if (t === 'relay') out.candRelay++
        else if (t === 'prflx') out.candPrflx++
      } else if (s.type === 'remote-candidate') {
        remotes[s.id] = s
      }
    })
    let pair = selectedPairId ? pairs[selectedPairId] : null
    if (!pair) {
      // Firefox has no RTCTransportStats — it flags the active pair with a non-standard
      // `selected`. Only then fall back to the first nominated/succeeded pair.
      const all = Object.values(pairs)
      pair = all.find((p) => p.selected === true)
        || all.find((p) => p.nominated || p.state === 'succeeded')
        || null
    }
    out.iceState = transportState || (pair ? pair.state : '') || ''
    if (pair) {
      out.availOutKbps = pair.availableOutgoingBitrate ? Math.round(pair.availableOutgoingBitrate / 100) / 10 : 0
      const lc = locals[pair.localCandidateId]
      const rc = remotes[pair.remoteCandidateId]
      if (lc) {
        out.selLocalType = candidateKind(lc)
        out.selProtocol = String(lc.protocol || '')
        out.selRelayProtocol = String(lc.relayProtocol || '')
      }
      if (rc) out.selRemoteType = String(rc.candidateType || '')
    }
  } catch { /* best-effort */ }
  return out
}
